import { useState } from 'react'
import { MessageCircle, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChatInterface } from './ChatInterface'

/**
 * ChatWidget - Floating chat button for public chatbot
 *
 * Features:
 * - Floating button in bottom-right corner
 * - Open/close chat interface
 * - Animated transitions
 * - Theme-aware styling
 */
export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  const handleToggle = () => {
    setIsOpen(!isOpen)
    if (isOpen) {
      setUnreadCount(0) // Clear unread count when opening
    }
  }

  return (
    <>
      {/* Floating Chat Button */}
      <motion.button
        onClick={handleToggle}
        className="fixed bottom-6 right-6 p-4 rounded-full shadow-lg hover:shadow-xl transition-shadow"
        style={{
          background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
          zIndex: 60
        }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label="Toggle chat"
      >
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X className="w-6 h-6 text-white" />
            </motion.div>
          ) : (
            <motion.div
              key="open"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative"
            >
              <MessageCircle className="w-6 h-6 text-white" />
              {unreadCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold"
                >
                  {unreadCount}
                </motion.span>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Chat Interface */}
      <AnimatePresence>
        {isOpen && (
          <ChatInterface
            onClose={() => setIsOpen(false)}
            onMessageReceived={() => {
              if (!isOpen) {
                setUnreadCount(prev => prev + 1)
              }
            }}
          />
        )}
      </AnimatePresence>
    </>
  )
}
